import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLogOut, FiActivity, FiDatabase, FiCloud, FiTool } from 'react-icons/fi';
import logger from '@/utils/logger';
import ApiLogPanel from './ApiLogPanel';
import ApiTestForm from './ApiTestForm';
import DebugPanel from './DebugPanel';

const tabs = [
  { id: 'ncb', label: 'NoCodeBackend', icon: FiDatabase },
  { id: 'dropbox', label: 'Dropbox API', icon: FiCloud },
  { id: 'debug', label: 'Debug Panel', icon: FiTool },
];

function ApiMonitorDashboard() {
  const [activeTab, setActiveTab] = useState('ncb');
  const [logs, setLogs] = useState([]);
  const [isLogPanelOpen, setLogPanelOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = logger.subscribe(setLogs);
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    logger.info('User logged out');
    localStorage.removeItem('promptly_session');
    navigate('/login');
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 min-h-[70vh]">
      <header className="flex flex-wrap justify-between items-center pb-6 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Monitor Platform</h1>
          <p className="text-gray-500 mt-1">Real-time API testing, logging, and debugging.</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={() => setLogPanelOpen(!isLogPanelOpen)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiActivity className="mr-2" />
            Logs ({logs.length})
          </button>
          <button
            onClick={handleLogout}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-900"
          >
            <FiLogOut className="mr-2" />
            Logout
          </button>
        </div>
      </header>
      
      <main className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center`}
              >
                <tab.icon className="mr-2 h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex mt-6">
          <div className="flex-1 pr-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'debug' ? <DebugPanel /> : <ApiTestForm apiType={activeTab} />}
              </motion.div>
            </AnimatePresence>
          </div>
          <AnimatePresence>
            {isLogPanelOpen && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '40%', opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ ease: 'easeInOut', duration: 0.4 }}
                className="overflow-hidden"
              >
                <div className="pl-4 border-l border-gray-200">
                  <ApiLogPanel logs={logs} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

export default ApiMonitorDashboard;